import { useTranslation } from "react-i18next";
import {
  ESenderRole,
  ETrasactionsType,
  IContractDetailsResponseData,
  Transactions,
  UserProfile,
} from "../../../../setup/api/services/contract/models";
import PartyTable from "../PartyTable";
import { EPARTY_STATUS } from "../../../../enums";
import styles from "./styles.module.scss";
import {
  findPartyForProfile,
  getCustomerAmount,
} from "../../../../setup/util/contract";

interface MainDetailsProps {
  milestone: IContractDetailsResponseData | undefined;
}

const MainDetails = ({ milestone }: MainDetailsProps) => {
  const { t } = useTranslation();

  const getPaymentMethodLabel = (
    transaction: Transactions
  ): string | undefined => {
    if (!transaction) return undefined;
    const method =
      transaction.type === "MANUAL_BANK_TRANSFER"
        ? "MANUAL_BANK_TRANSFER"
        : transaction.paymentMethod;
    if (!method) return undefined;
    switch (method) {
      case "CARD":
        return t("lbl_bank_card");
      case "APPLE_PAY":
        return t("lbl_apple_pay");
      case "STCPAY":
        return t("lbl_stc_pay");
      case "TABBY":
        return t("lbl_pay_with_tabby");
      case "TAMARA":
        return t("lbl_pay_with_tamara");
      case "MANUAL_BANK_TRANSFER":
        return t("lbl_bank_transfer");
      case "CONTRACT_TRANSFER":
        return t("lbl_contract_transfer");
      default:
        return method;
    }
  };

  const customerRows = (milestone?.partiesProfiles?.CUSTOMER || []).map(
    (party) => {
      const currentParty = findPartyForProfile(
        milestone?.parties,
        party,
        ESenderRole.CUSTOMER
      );
      const paymentTransaction = milestone?.transactions?.find(
        (transaction) =>
          transaction?.fromUserId?.toString() ===
            currentParty?.userId?.toString() &&
          (transaction?.type === "CUSTOMER_PAY" ||
            transaction?.type === "MANUAL_BANK_TRANSFER") &&
          transaction?.status === "SUCCESS"
      );
      const onBehalfTransaction = milestone?.transactions?.find(
        (transaction) =>
          transaction?.fromUserId?.toString() ===
            party?.userIdentification?.toString() &&
          transaction?.paidOnBehalf === true &&
          (transaction.status === "PENDING" ||
            transaction.status === "SUCCESS") &&
          (transaction.type === ETrasactionsType.MANUAL_BANK_TRANSFER ||
            transaction.type === ETrasactionsType.CUSTOMER_PAY)
      );
      return {
        id: party?.id?.toString() || "",
        name: party?.profile?.fullName || "",
        phoneNumber: party?.phoneNumber || currentParty?.phoneNumber || "",
        amount: getCustomerAmount(milestone, currentParty, party),
        status: currentParty?.status,
        type: "customer",
        image: party?.profile?.photoUrl || "",
        paymentMethod: paymentTransaction
          ? getPaymentMethodLabel(paymentTransaction)
          : undefined,
        onBehalfByName: onBehalfTransaction?.onBehalfByName,
      };
    }
  );

  const toPartyRow =
    (role: ESenderRole.PROVIDER | ESenderRole.BROKER) =>
    (party: Partial<UserProfile>) => {
      const currentParty = findPartyForProfile(milestone?.parties, party, role);
      return {
        id: party?.id?.toString() || "",
        name: party?.profile?.fullName || "",
        phoneNumber: party?.phoneNumber || currentParty?.phoneNumber || "",
        amount: currentParty?.amount || 0,
        status:
          milestone?.cashOutAmountList?.find(
            (item) =>
              !!currentParty?.userId &&
              item?.userIdentification === currentParty?.userId
          )?.settlementAmountStatus === EPARTY_STATUS.AVAILABLE_BALANCE
            ? EPARTY_STATUS.AVAILABLE_BALANCE
            : currentParty?.status,
        type: "provider",
        image: party?.profile?.photoUrl || "",
      };
    };

  const showOnBehalfColumn = customerRows.some((row) => !!row.onBehalfByName);

  // The payees table carries columns the receivers and brokers tables do not.
  // Those two reserve the same tracks so the columns all three share — party,
  // phone number, value, status — line up vertically down the page.
  const reservedPayeeColumns = {
    reservePaymentMethod: true,
    reserveOnBehalf: showOnBehalfColumn,
  };

  return (
    <>
      {customerRows.length > 0 && (
        <div className={styles.partyContainer}>
          <h2>{t("lbl_payees")}</h2>

          <PartyTable
            rows={customerRows}
            columns={{
              showStatus: true,
              showPaymentMethod: true,
              showOnBehalf: showOnBehalfColumn,
            }}
          />
        </div>
      )}

      {(milestone?.partiesProfiles?.PROVIDER || []).length > 0 && (
        <div className={styles.partyContainer}>
          <h2>{t("lbl_receivers")}</h2>

          <PartyTable
            rows={(milestone?.partiesProfiles?.PROVIDER || []).map(
              toPartyRow(ESenderRole.PROVIDER)
            )}
            columns={{
              showStatus: true,
              ...reservedPayeeColumns,
            }}
          />
        </div>
      )}

      {(milestone?.partiesProfiles?.BROKER || []).length > 0 && (
        <div className={styles.partyContainer}>
          <h2>{t("lbl_brokers")}</h2>

          <PartyTable
            rows={(milestone?.partiesProfiles?.BROKER || []).map(
              toPartyRow(ESenderRole.BROKER)
            )}
            columns={{
              showStatus: true,
              ...reservedPayeeColumns,
            }}
          />
        </div>
      )}
    </>
  );
};

export default MainDetails;
